<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="items"
      :pagination.sync="sortOptions"
      :rows-per-page-items="[-1]"
      :loading="loading"
      hide-actions
      class="elevation-1"
    >
      <template v-slot:headers="props">
        <th class="text-xs-center px-2">
          <v-checkbox
            v-if="bulkEditMode"
            :input-value="selectedAll"
            :indeterminate="props.indeterminate"
            @click.stop="toggleAll"
            primary
            hide-details
          />
        </th>
        <th
          v-for="header in props.headers"
          :key="header.text"
          :class="[
            'column sortable px-2',
            sortOptions.descending ? 'desc' : 'asc',
            header.value === sortOptions.sortBy ? 'active' : '',
            header.align === 'center' ? 'text-xs-center' : '',
            header.align === 'right' ? 'text-xs-right' : '',
            header.align === 'left' ? 'text-xs-left' : '',
          ]"
          :aria-sort="header.sortable ? '' : 'none'"
          @click="changeSort(header)"
        >
          {{ header.text }}
          <v-icon v-if="header.sortable" small>arrow_upward</v-icon>
        </th>
      </template>

      <template slot="items" slot-scope="props">
        <tr>
          <td v-if="!bulkEditMode" class="text-xs-left no-wrap px-2">
            <v-btn
              v-if="props.item.requestType !== 'delete'"
              :disabled="!props.item.isApprovable"
              :loading="isLoading(props.item)"
              @click="approve(props.item)"
              small
              color="success"
              >承認する</v-btn
            >
            <v-btn
              v-else
              :disabled="!props.item.isDeletable"
              :loading="isLoading(props.item)"
              @click="confirmDelete(props.item)"
              small
              color="error"
              >削除する</v-btn
            >
            <v-btn
              :disabled="!props.item.isRejectable"
              :loading="isLoading(props.item)"
              @click="confirmReject(props.item)"
              small
              >却下する</v-btn
            >
          </td>
          <td v-if="bulkEditMode" class="text-xs-center no-wrap px-2">
            <v-checkbox
              v-if="props.item.isApprovable"
              :input-value="isSlected(props.item)"
              @click.stop="toggle(props.item)"
              hide-details
              primary
            />
          </td>
          <td class="text-xs-center no-wrap">
            <Status
              :status="props.item.requestStatus"
              perspective="agency"
              with-color
            />
          </td>
          <td class="text-xs-center px-2">
            <Type :type="props.item.requestType" />
          </td>
          <td class="text-xs-left no-wrap px-2">
            {{ props.item.agencyName }}
          </td>
          <td class="text-xs-left no-wrap px-2">
            {{ props.item.advertiserName }}
          </td>
          <td class="text-xs-center no-wrap">
            <AppStatus
              :disabled="true"
              :value="props.item.advertiserStatus"
              :showText="true"
              with-icon
            />
          </td>
          <td class="text-xs-left no-wrap px-2">
            {{ props.item.insertionOrderName }}
          </td>
          <td class="text-xs-center no-wrap px-2">
            <AppDateTime :value="props.item.startTime" />
          </td>
          <td class="text-xs-center no-wrap px-2">
            <AppDateTime :value="props.item.endTime" />
          </td>
          <td class="text-xs-right no-wrap px-2">
            <AppCurrency :value="props.item.budget" />
            <br />
            <NetBudget :value="props.item.netBudget" />
          </td>
          <td class="text-xs-center px-2">
            <AppDateTime :value="props.item.requestTime" />
          </td>
          <td class="text-xs-center px-2">
            <v-btn @click="showDetail(props.item)" small>詳細</v-btn>
          </td>
        </tr>
      </template>
      <template v-slot:no-data>
        <div class="text-xs-center">データがありません。</div>
      </template>
    </v-data-table>
    <DetailModal v-model="detailModalVisible" :request="focusItem" />
    <RejectModal v-model="rejectModalVisible" :request="focusItem" />
    <DeleteModal v-model="deleteModalVisible" :request="focusItem" />
  </div>
</template>

<script>
import NetBudget from './NetBudget.vue'
import cloneDeep from 'lodash/cloneDeep'
import unionBy from 'lodash/unionBy'
import differenceBy from 'lodash/differenceBy'
import Status from '@/components/Shared/Presentation/Request/Status'
import Type from '@/components/Shared/Presentation/Request/Type'
import DetailModal from './DetailModal'
import RejectModal from './RejectModal'
import DeleteModal from './DeleteModal'
import { actions } from '@/vuex/modules/admin/request/budget/constants'
import {
  actions as snackbarActions,
  snackbarType,
} from '@/vuex/modules/ui/snackbar/constants'

export default {
  name: 'BudgetRequestTable',
  components: {
    Status,
    Type,
    DetailModal,
    RejectModal,
    DeleteModal,
    NetBudget,
  },
  props: {
    items: Array,
    approvableItems: Array,
    inputSortOptions: Object,
    loading: Boolean,
    bulkEditMode: Boolean,
    selectedItems: Array,
  },
  data() {
    return {
      sortOptions: {},
      focusItem: null,
      detailModalVisible: false,
      rejectModalVisible: false,
      deleteModalVisible: false,
      loadingItems: [],
    }
  },
  computed: {
    headers() {
      return [
        {
          text: 'ステータス',
          value: 'requestStatus',
          align: 'center',
          sortable: true,
        },
        {
          text: '申請種別',
          value: 'requestType',
          align: 'center',
          sortable: true,
        },
        {
          text: '代理店名',
          value: 'agencyName',
          align: 'center',
          sortable: true,
        },
        {
          text: '広告主名',
          value: 'advertiserName',
          align: 'center',
          sortable: true,
        },
        {
          text: '広告主ステータス',
          value: 'advertiserStatus',
          align: 'center',
          sortable: false,
        },
        {
          text: '案件名',
          value: 'insertionOrderName',
          align: 'center',
          sortable: true,
        },
        {
          text: '配信開始日時',
          value: 'startTime',
          align: 'center',
          sortable: true,
        },
        {
          text: '配信終了日時',
          value: 'endTime',
          align: 'center',
          sortable: true,
        },
        { text: '予算', value: 'budget', align: 'center', sortable: true },
        {
          text: '申請日時',
          value: 'requestTime',
          align: 'center',
          sortable: true,
        },
        { text: '詳細情報', value: 'detail', align: 'center', sortable: false },
      ]
    },
    selectedAll() {
      return (
        this.approvableItems.length > 0 &&
        differenceBy(this.approvableItems, this.selectedItems, 'id').length ===
          0
      )
    },
  },
  created() {
    this.sortOptions = cloneDeep(this.inputSortOptions)
  },
  methods: {
    showDetail(budgetRequest) {
      this.focusItem = budgetRequest
      this.detailModalVisible = true
    },
    confirmReject(budgetRequest) {
      this.focusItem = budgetRequest
      this.rejectModalVisible = true
    },
    confirmDelete(budgetRequest) {
      this.focusItem = budgetRequest
      this.deleteModalVisible = true
    },
    isSlected(item) {
      const index = this.selectedItems.findIndex(
        selectedItem => selectedItem.id === item.id,
      )
      return index !== -1
    },
    toggle(item) {
      this.$store.dispatch(actions.TOGGLE_SELECTED_ITEM, item)
    },
    toggleAll() {
      this.$store.dispatch(actions.TOGGLE_ALL_SELECTED_ITEMS)
    },
    changeSort(header) {
      if (!header.sortable) return

      const column = header.value
      if (this.sortOptions.sortBy === column) {
        this.sortOptions.descending = !this.sortOptions.descending
      } else {
        this.sortOptions.sortBy = column
        this.sortOptions.descending = false
      }
      this.$store.dispatch(actions.UPDATE_SORT_OPTIONS, this.sortOptions)
    },
    async approve(item) {
      try {
        this.setLoading(item)
        await this.$store.dispatch(actions.APPROVE_ITEM, { item })
        this.$store.dispatch(snackbarActions.SHOW_SNACKBAR, {
          type: snackbarType.SUCCESS,
          message: '承認しました',
        })
        this.$store.dispatch(actions.SEARCH)
      } finally {
        this.finishLoading(item)
      }
    },
    setLoading(item) {
      this.loadingItems = unionBy(this.loadingItems, [item], i => i.id)
    },
    finishLoading(item) {
      this.loadingItems = differenceBy(this.loadingItems, [item], i => i.id)
    },
    isLoading(item) {
      return this.loadingItems.find(i => i.id === item.id) != null
    },
  },
}
</script>
